import type { LeadFormPublicConfig, RecommendedProductSummary } from './types';

export interface ActiveFormsResult {
  forms: LeadFormPublicConfig[];
  /** Server-resolved via GeoIpService; null when it's unconfigured or couldn't resolve this
   * visitor's IP — targeting.ts treats null the same as "no country restriction matched". */
  visitorCountry: string | null;
}

/**
 * fetch()-only, no axios — every dependency is bundle weight on a third-party page. Talks to
 * PublicLeadFormController's permitAll `/api/v1/forms/{projectSlug}` routes; cross-origin access is
 * gated entirely by PublicFormsCorsFilter on the backend, not by anything this client does.
 */
export class FormsApiClient {
  constructor(
    private readonly apiBaseUrl: string,
    private readonly projectSlug: string,
  ) {}

  async fetchActiveForms(): Promise<ActiveFormsResult> {
    const empty: ActiveFormsResult = { forms: [], visitorCountry: null };
    const response = await fetch(`${this.apiBaseUrl}/api/v1/forms/${this.projectSlug}/active`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return empty;
    const body = (await response.json()) as { data?: ActiveFormsResult };
    return body.data ?? empty;
  }

  /** Product cards for a form's `theme.recommendations` widget. Always safe to call — a form with
   * nothing configured just gets an empty array back, not an error. */
  async fetchRecommendations(formSlug: string): Promise<RecommendedProductSummary[]> {
    const response = await fetch(`${this.apiBaseUrl}/api/v1/forms/${this.projectSlug}/${formSlug}/recommendations`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return [];
    const body = (await response.json()) as { data?: RecommendedProductSummary[] };
    return body.data ?? [];
  }

  async submit(
    formSlug: string,
    data: Record<string, unknown>,
    variantId: string | null,
  ): Promise<boolean> {
    const response = await fetch(`${this.apiBaseUrl}/api/v1/forms/${this.projectSlug}/${formSlug}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data,
        pageUrl: window.location.href,
        referrer: document.referrer || null,
        variantId,
      }),
    });
    return response.ok;
  }

  /** Fire-and-forget impression beacon. Never awaited by callers.
   *
   * Deliberately fetch(keepalive) rather than navigator.sendBeacon(): sendBeacon has no way to
   * request credentials: 'omit' — the spec always sends it as a credentialed (`include`) CORS
   * request — and PublicFormsCorsFilter deliberately never returns Access-Control-Allow-Credentials
   * (see its class doc), so every cross-origin sendBeacon call here was silently and permanently
   * CORS-blocked. Since every real deployment of this widget is cross-origin (the API always lives
   * on a different host than wherever the embed sits, including the platform's own pages domain),
   * that wasn't a rare edge case — impressions never recorded, for any project, ever. fetch's
   * default credentials mode already omits credentials cross-origin, matching submit()'s
   * already-working behavior above; kept explicit here since a future accidental `credentials:
   * 'include'` would silently reintroduce this exact bug. */
  sendImpression(formSlug: string, variantId: string | null): void {
    const url = `${this.apiBaseUrl}/api/v1/forms/${this.projectSlug}/${formSlug}/impression`;
    const payload = JSON.stringify({ variantId, pageUrl: window.location.href });

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Impressions are best-effort analytics — a dropped beacon must never surface to the visitor.
    });
  }
}
